/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */

// import Vue from 'vue';
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { HttpError, PMSError, ValidationError } from '@/errors';
import Vue from 'vue';

const endpoint = `${apiEndpoint}/partner`;

const state = {
  group: null,
  partners: null,
  suppliers: null,
  distributors: null,
  pending: false,
  error: null,
  updateError: null,
  partnersPending: false,
  countries: null,
  businessCategories: null,
  channels: null,
  partnersList: [],
};

const getters = {
  loaded: (state) => state.partners != null && !state.partnersPending,
  group: (state, getters) => (getters.loaded ? state.group : {}),
  partners: (state) => state.partners,
  suppliers: (state) => state.suppliers,
  distributors: (state) => state.distributors,
  channels: (state) => state.channels,
  countries: (state) => state.countries,
  businessCategories: (state) => state.businessCategories,
  partnersPending: (state) => state.partnersPending,
  partnersList: (state) => state.partnersList,
  hasPartners: (state, getters) => (getters.loaded ? state.partners?.data?.length > 0 : false),
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.group = null;
    }
    state.error = null;
    state.pending = true;
    state.invalid = false;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.partnersPending = true;
  },
  afterUpdate(state) {
    state.partnersPending = false;
  },
  data(state, { data = null, error = null, update = false } = {}) {
    if (!update) {
      state.pending = false;
    } else {
      state.partnersPending = false;
    }
    state.partners = data;
    state.error = error;
  },
  pageData(state, {
    channels, countries, suppliers, distributors, businessCategories,
  }) {
    state.suppliers = suppliers;
    state.channels = channels;
    state.countries = countries;
    state.distributors = distributors;
    state.businessCategories = businessCategories;
  },
  beforePartnersRequest(state) {
    state.partnersPending = true;
  },
  afterPartnersRequest(state) {
    state.partnersPending = false;
  },
  addPartner(state, partner) {
    if (state.partners == null) {
      state.partners = [];
    }
    state.partners.content.push(partner);
    state.partnersPending = false;
  },
  updatePartner(state, partner) {
    const idx = state.partners.content.findIndex(({ id }) => id === partner.id);
    if (idx !== -1) {
      Vue.set(state.partners.content, idx, partner);
    }
    state.partnersPending = false;
  },
  deletePartner(state, pId) {
    const idx = state.partners.content.findIndex(({ id }) => Number(id) === Number(pId));
    if (idx > -1) {
      state.partners.content.splice(idx, 1);
    }
    state.partnersPending = false;
  },
  partnersList(state, data) {
    state.partnersList = data;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  // commit('update', err);
  throw err;
}

const actions = {
  async getPartner({ commit }, id) {
    commit('beforePartnersRequest');
    try {
      const { data: partner } = await ApiService.get(`${endpoint}/${id}`);
      commit('updateProperty', partner);
      return partner;
    } catch (error) {
      commit('afterPartnersRequest');
      handleApiError(commit, error);
      return null;
    }
  },
  async createPartner({ commit }, payload) {
    commit('beforePartnersRequest');
    try {
      const { data: partner } = await ApiService.post(`${endpoint}`, payload);
      commit('addPartner', partner);
      return partner;
    } catch (error) {
      commit('afterPartnersRequest');
      handleApiError(commit, error);
      return null;
    }
  },
  async updatePartner({ commit }, data) {
    commit('beforePartnersRequest');
    try {
      const { id } = data;
      const payload = data;
      delete payload.id;
      const { data: partner } = await ApiService.put(`${endpoint}/${id}`, payload);
      commit('updatePartner', partner);
    } catch (error) {
      commit('afterPartnersRequest');
      handleApiError(commit, error);
    }
  },
  async updatePartnerTranslations({ commit }, data) {
    commit('beforePartnersRequest');
    try {
      const { id } = data;
      const payload = data;
      delete payload.id;
      const { data: partner } = await ApiService.put(`${endpoint}/${id}/translations`, payload);
      commit('updatePartner', partner);
    } catch (error) {
      commit('afterPartnersRequest');
      handleApiError(commit, error);
    }
  },
  async deletePartner({ commit }, id) {
    commit('beforePartnersRequest');
    try {
      await ApiService.delete(`${endpoint}/${id}`);
      commit('deletePartner', id);
    } catch (error) {
      commit('afterPartnersRequest');
      throw handleApiError(commit, error);
    }
  },
  async fetchPartners({ commit }, payload) {
    commit('beforePartnersRequest');
    try {
      const page = payload?.page || 1;
      const perPage = payload?.perPage || 10;
      const filter = payload?.filter ? payload.filter : '';
      const order = payload?.order ? payload.order : 'name';
      const sort = payload?.sort ? payload.sort : 'asc';
      const query = {
        page,
        perPage,
        filter,
        order,
        sort,
      };
      const queryParams = (new URLSearchParams(query)).toString();
      const { data } = await ApiService.get(`${endpoint}?${queryParams}`);
      commit('data', { data });
      commit('afterPartnersRequest');
      return data;
    } catch (error) {
      commit('afterPartnersRequest');
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
  async fetchDataSupplyPartners({ commit }, payload) {
    commit('beforePartnersRequest');
    try {
      const { data } = await ApiService.post(`${endpoint}/data-supply`, payload);
      commit('data', { data });
      commit('afterPartnersRequest');
      return data;
    } catch (error) {
      commit('afterPartnersRequest');
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
  async fetchPartnerPageData({ commit }, payload) {
    try {
      const { data } = await ApiService.get(`${endpoint}/page-data`, payload);
      commit('pageData', data);
      return data;
    } catch (error) {
      commit('afterPartnersRequest');
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
  async fetchAllPartners({ commit }, payload) {
    try {
      const { data } = await ApiService.get(`${endpoint}/all`, payload);
      commit('partnersList', data);
    } catch (error) {
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
